import { useState, useEffect } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { Workflow, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail, API } from "@/lib/api";

export default function Login() {
  const { user, login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [logoUrl, setLogoUrl] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    // Check if custom logo exists (public endpoint, no auth)
    fetch(`${API}/branding/logo/exists`)
      .then((r) => r.json())
      .then((d) => { if (d.exists) setLogoUrl(`${API}/branding/logo?t=${Date.now()}`); })
      .catch(() => {});
  }, []);

  if (user) return <Navigate to="/" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/");
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white">
      <div className="flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-sm">
          <div className="flex items-center gap-2 mb-10">
            {logoUrl ? (
              <img src={logoUrl} alt="Logo" className="w-10 h-10 rounded-md object-contain" />
            ) : (
              <div className="w-10 h-10 rounded-md bg-[#0A2540] flex items-center justify-center">
                <Workflow className="w-5 h-5 text-white" strokeWidth={1.8} />
              </div>
            )}
            <div>
              <div className="text-base font-semibold tracking-tight text-[#0F172A]">FRESHNEL Middleware Accurate x Pancake</div>
              <div className="text-xs text-[#64748B]">Middleware Console</div>
            </div>
          </div>

          <h1 className="text-3xl font-semibold tracking-tight text-[#0F172A]">Masuk ke akun</h1>
          <p className="text-sm text-[#64748B] mt-2 mb-8">
            Akses dashboard middleware Accurate.id × Pancake.id
          </p>

          <form onSubmit={onSubmit} className="space-y-4" data-testid="login-form">
            <div>
              <label className="block text-xs font-medium text-[#475569] mb-1.5">Email</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email-input"
                className="w-full bg-white border border-[#E2E8F0] rounded-md px-3 py-2 text-sm focus:border-[#0066FF] focus:ring-1 focus:ring-[#0066FF] outline-none transition-all"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-[#475569] mb-1.5">Password</label>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password-input"
                className="w-full bg-white border border-[#E2E8F0] rounded-md px-3 py-2 text-sm focus:border-[#0066FF] focus:ring-1 focus:ring-[#0066FF] outline-none transition-all"
              />
            </div>

            {error && (
              <div data-testid="login-error" className="text-sm text-[#DC2626] bg-[#FEE2E2] px-3 py-2 rounded-md">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit-button"
              className="w-full bg-[#0A2540] text-white hover:bg-[#1E3A8A] rounded-md px-4 py-2.5 text-sm font-medium transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              Masuk
            </button>

          </form>
        </div>
      </div>

      {/* CSS gradient background - no external image download needed */}
      <div className="hidden lg:block relative overflow-hidden bg-gradient-to-br from-[#0A2540] via-[#1E3A5F] to-[#0A2540]">
        {/* Decorative shapes */}
        <div className="absolute inset-0">
          <div className="absolute top-[-10%] right-[-5%] w-[60%] h-[60%] rounded-full bg-[#1E4D8A]/20 blur-3xl" />
          <div className="absolute bottom-[-15%] left-[-10%] w-[50%] h-[50%] rounded-full bg-[#0066FF]/10 blur-3xl" />
          <div className="absolute top-[40%] right-[20%] w-[30%] h-[30%] rounded-full bg-[#38BDF8]/10 blur-2xl" />
        </div>
        <div className="h-full w-full flex items-end p-12 relative z-10">
          <div className="max-w-md">
            <div className="text-xs font-bold tracking-[0.18em] uppercase text-[#38BDF8] mb-3">
              Otomatisasi Penagihan
            </div>
            <div className="text-2xl font-semibold tracking-tight text-white leading-snug">
              Kirim invoice & reminder pembayaran otomatis ke WhatsApp pelanggan, tanpa input manual.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
